import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core'
import { ContextService } from 'app/services'
import { HistoricalService } from 'app/services/historical.service'
import { Colors } from 'app/common/config/constants'
import { TranslateService } from '@ngx-translate/core'
import {
  sum,
  select,
  scaleBand,
  max,
  scaleLinear,
  range as d3Range,
  line as d3Line,
  curveCardinal,
  format,
} from 'd3'
import { HideZeroValuesPipe } from 'app/common/hide-zero-values.pipe'
import { WidgetBaseComponent } from '../widget-base/widget-base.component'

@Component({
  standalone: false,
  selector: 'app-floating-widget',
  templateUrl: './floating-widget.component.html',
  styleUrls: ['./floating-widget.component.scss'],
})
export class FloatingWidgetComponent implements OnInit, AfterViewInit {
  private _targets: any = {}
  private _viewReady = false
  collapsed = false
  selectorOpened = false
  config: any = {
    xScale: [],
    labels: [],
    colorScale: [(i) => [Colors.colorAccent]],
    mode: 'stacked',
    barWidth: 50,
    unit: '%',
  }

  options: any[] = []
  current = null
  _labels: any = {}

  selectedCategory = null
  data = []

  get targets(): any {
    return this._targets
  }

  @Input()
  set targets(value: any) {
    this._targets = value
    if (this._viewReady) {
      this._prepareComponent()
    }
  }

  @Input() title = ''
  @Input() loading = false

  private _comparison = false
  get comparison(): boolean {
    return this._comparison
  }

  @Input()
  set comparison(value: boolean) {
    this._comparison = value
    this.parseData()
  }

  @Output() onCollapse = new EventEmitter()

  @ViewChild('tooltip', { static: true }) tooltip: ElementRef
  @ViewChild('svg', { static: true }) svg: ElementRef

  @ViewChild('container', { static: true }) container: WidgetBaseComponent

  @HostListener('window:resize', [])
  onResize() {
    this.draw()
  }

  get categories() {
    return this.data[0] && this.data[0][0] ? this.data[0][0].values : []
  }

  get legends() {
    return Array.isArray(this.data)
      ? this.data.map((s) => s[0]?.values).filter(Boolean)
      : []
  }

  get currentDelivery() {
    return this.context.vertical.delivery
  }

  get hasCampaignCreation() {
    return (
      this.currentDelivery && this.currentDelivery.config.hasCampaignCreation
    )
  }

  constructor(
    private el: ElementRef,
    private translateService: TranslateService,
    public context: ContextService,
    private historicalService: HistoricalService,
    private hideZero: HideZeroValuesPipe,
  ) {}

  parseData() {
    this.setConfigChart()
    if (this.current) {
      const target = this.targets[this.current.value]
      if (!target) return
      if (this.comparison) {
        this.data = (Array.isArray(target) ? target : [target])
          .filter((val) => Array.isArray(val?.values))
          .map((val) => this.prepareData(val.values, this.config))
      } else {
        if (!Array.isArray(target.values)) return
        this.data = [this.prepareData(target.values, this.config)]
      }
    }
  }

  ngOnInit() {
    if (this.hasCampaignCreation) {
      this.container.toggleWidget()
    }
    this._prepareComponent()
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this._viewReady = true
      this._prepareComponent()
    }, 250)
  }

  prepareData(value: any[], config) {
    return value
      .slice()
      .reverse()
      .map((elem) => {
        elem.total = Array.isArray(elem.values)
          ? sum(elem.values, (d) => d.value)
          : elem.value
        elem.values = elem.values || [
          { category: 'nvisitors', value: elem.value },
        ]
        this.sortCategories(elem.values, config)
        elem.values = elem.values.map((v, i, arr) => {
          const prev = arr[i - 1]
          v.delivery_id = elem.delivery_id
          v.name = elem.name
          v.filter = elem.filter
          v.bottom = prev ? prev.top : 0
          v.top = v.bottom + (v.value || 0)
          v.category_name = this.config.labels[v.category] || v.category
          return v
        })
        return elem
      })
  }

  sortCategories(values, config) {
    values.sort((a, b) => {
      const keyA = a.id.replace(
        `${this.context.keyIndicator.value}_${a.key}_`,
        '',
      )
      const keyB = b.id.replace(
        `${this.context.keyIndicator.value}_${b.key}_`,
        '',
      )
      return (
        config.categoriesSort.indexOf(keyA) -
        config.categoriesSort.indexOf(keyB)
      )
    })
  }

  getColor(cat, idx = 0, serieIdx = 0) {
    return this.config.colorScale[serieIdx]
      ? this.config.colorScale[serieIdx](idx)
      : this.config.colorScale[0](idx)
  }

  draw() {
    if (!this.data.length) {
      return
    }

    this.setConfigChart()
    const containerDims =
      this.svg.nativeElement.parentElement.getBoundingClientRect()
    if (!containerDims) {
      console.log(
        '[Bar Chart] Aborting render: container dimensions are not available',
      )
      return
    }
    let tooltip
    if (this.tooltip) {
      tooltip = select(this.tooltip.nativeElement)
    }
    const svg = select(this.svg.nativeElement)
    const margin = { top: 26, right: 0, bottom: 60, left: 0 }
    const width = containerDims.width - margin.left - margin.right
    const height = containerDims.height - margin.top - margin.bottom
    svg.attr('width', containerDims.width).attr('height', containerDims.height)
    svg.selectAll('g').remove()
    const g = svg
      .append('g')
      .attr('class', 'center')
      .attr('transform', `translate(${margin.left}, ${margin.top})`)

    const xScale = scaleBand().domain(this.config.xScale).range([0, width])

    let yMax = 100
    if (this.config.mode === 'linear') {
      yMax = Math.max(
        ...this.data.map((serie) =>
          max(serie, (d) => max(d.values, (d2) => d2.value)),
        ),
      )
    }
    const yScale = scaleLinear().rangeRound([height, 0]).domain([0, yMax])

    this.data = this.data.map((s) => {
      return s.map((d) => {
        d.values = d.values.map((v, i, arr) => {
          const prev = arr[i - 1]
          v.bottom = prev ? prev.top : 0
          const h = v.total ? height * (v.value / v.total - 0.02) : 0
          v.top = v.bottom + h
          return v
        })
        return d
      })
    })
    this.data.forEach((s) => {
      s.sort(
        (a, b) =>
          this.config.xScale.indexOf(a.filter) -
          this.config.xScale.indexOf(b.filter),
      )
    })

    const barMargin =
      (width - this.config.barWidth * this.config.xScale.length) /
      this.config.xScale.length /
      2

    const barMarginInner = 3

    const bgGapPercent = 0.25

    const range = d3Range(0, 1 + bgGapPercent, bgGapPercent) // [0, 0.25, ..., 1]
    g.append('g')
      .attr('class', 'y-background')
      .selectAll('line')
      .data(range)
      .enter()
      .append('line')
      .attr('stroke', Colors.colorN80)
      .attr('x1', 0)
      .attr('x2', width)
      .attr('y1', (d) => d * height)
      .attr('y2', (d) => d * height)

    if (this.config.mode === 'linear') {
      this.data.forEach((_data, idx) => {
        const data = this.parseToLines(_data)
        g.selectAll('.line')
          .data(data)
          .enter()
          .append('path')
          .attr('fill', 'none')
          .attr('stroke', (d, i) => this.getColor(d, i))
          .attr('stroke-width', 1.5)
          .attr('d', (d) => {
            const line = d3Line()
              .curve(curveCardinal.tension(0.5))
              .x((p) => xScale(p.filter) + barMargin)
              .y((p) => yScale(+p.value))
            return line(d)
          })
        // Add the scatterplot
        const dotData = []
        _data.forEach((row) => {
          row.values.forEach((d, rIdx) => {
            dotData.push({
              ...d,
              idx: rIdx,
              filter: row.filter,
            })
          })
        })
        g.selectAll('dot')
          .data(dotData)
          .enter()
          .append('circle')
          .style('cursor', 'pointer')
          .attr('fill', (d, i) => this.getColor(d, d.idx, idx))
          .attr('stroke', (d, i) => this.getColor(d, d.idx, idx))
          .attr('r', 3.5)
          .attr('cx', (d, i, nodes) => xScale(d.filter) + barMargin)
          .attr('cy', (d, i, nodes) => yScale(+d.value))
          .on('mouseenter', (d, i, nodes) => {
            const x = xScale(d.filter)
            const y = yScale(d.perc) - 60
            tooltip.style('left', `${x}px`)
            tooltip.style('top', `${y}px`)
            tooltip.style('bottom', `auto`)
            tooltip.style('cursor', `pointer`)
            tooltip
              .select('.name')
              .text(
                this.translateService.instant(this.current.label || '') + ' ',
              )
            tooltip
              .select('.category')
              .text(this.translateService.instant(d.category_name))
            tooltip.select('.value').text(() => {
              return format(',.0f')(d.value)
            })
            tooltip.classed('hidden', false)
          })
          .on('mousemove', (d, i, nodes) => {
            const x = xScale(d.filter)
            const y = yScale(d.perc) - 60
            tooltip.style('left', `${x}px`)
            tooltip.style('bottom', `auto`)
            tooltip.style('top', `${y}px`)
          })
          .on('mouseout', (d, i, nodes) => {
            const path = select(nodes[i].parentElement).select('.hover-path')
            path.style('display', 'none')
            tooltip.classed('hidden', true)
          })
      })
    } else if (['grouped', 'stacked'].includes(this.config.mode)) {
      this.data.forEach((serie, idx) => {
        const barGroups = g
          .append('g')
          .attr('class', 'bars')
          .selectAll('g')
          .data(serie)
          .enter()
          .append('g')
          .attr('transform', (d) => {
            let x =
              xScale(d.filter) + barMargin + (this.config.barWidth + 12) * idx
            x = x - ((this.data.length - 1) * (this.config.barWidth + 12)) / 2
            return `translate(${x}, 0)`
          })
          .attr('class', (d) => `bar-group-${d.category}`)

        const rects = barGroups
          .selectAll('rect')
          .data((d) => d.values)
          .enter()
          .append('rect')
          .attr('class', (d) => `inner-bar ${d.category}`)
          .attr('stroke-width', '0.2px')
          .attr('stroke', 'white')
          .attr('fill', (d, i) => this.getColor(d, i, idx))
          .on('mouseenter', (d, i, nodes) => {
            const rect = select(nodes[i])
            const path = select(nodes[i].parentElement).select('.hover-path')
            const x = +rect.attr('x') + 1,
              y = +rect.attr('y') + 1,
              w = +rect.attr('width') - 2,
              h = +rect.attr('height') - 2
            path.style('display', null)
            path.attr('d', `M${x} ${y} h ${w} v ${h} h ${-w} L ${x} ${y}`)
            tooltip
              .select('.name')
              .text(
                this.translateService.instant(this.current.label || '') + ' ',
              )
            tooltip
              .select('.category')
              .text(this.translateService.instant(d.category_name))
            tooltip.select('.value').text(() => {
              let label = format(',.0f')(d.value)
              if (d.perc !== undefined) {
                label += ` - ${format(',.2f')(d.perc)}%`
              }
              return label
            })
            tooltip.classed('hidden', false)
          })
          .on('mousemove', (d, i, nodes) => {
            const bounds = nodes[i].parentElement.getBoundingClientRect()
            const x = xScale(d.filter) + barMargin - bounds.width / 2
            const y = bounds.height + 50
            tooltip.style('left', `${x}px`)
            tooltip.style('bottom', `${y}px`)
            tooltip.style('top', `auto`)
          })
          .on('mouseout', (d, i, nodes) => {
            const path = select(nodes[i].parentElement).select('.hover-path')
            path.style('display', 'none')
            tooltip.classed('hidden', true)
          })

        if (this.config.mode === 'stacked') {
          rects
            .attr('y', (d) => yScale(d.top))
            .attr('x', 0)
            .attr('height', (d) =>
              Math.max(2, yScale(d.bottom) - yScale(d.top) - 0.2),
            )
            .attr('width', this.config.barWidth)
        } else if (this.config.mode === 'grouped') {
          rects
            .attr('width', this.config.barWidth / this.categories.length)
            .attr('height', (d) => height - yScale(d.perc))
            .attr('y', (d) => yScale(d.perc))
            .attr(
              'x',
              (d, i) =>
                i *
                (this.config.barWidth / this.categories.length +
                  barMarginInner),
            )
        }
      })
    } else {
      throw new Error(
        `Unsupported bar-chart mode: ${this.config.mode}. Supported values are 'grouped' and 'stacked'`,
      )
    }

    g.append('g')
      .attr('class', 'x axis')
      .attr('transform', `translate(0, ${height})`)
      .selectAll('text')
      .data(this.data[0])
      .enter()
      .append('text')
      .attr('text-anchor', 'middle')
      .attr('x', (d) => xScale(d.filter) + this.config.barWidth / 2 + barMargin)
      .attr('dy', '20px')
      .text((d) => d.filter)

    // Add X axis label:
    g.append('text')
      .attr('class', 'x axis')
      .attr('transform', `translate(${width / 2}, ${height + margin.top + 10})`)
      .attr('text-anchor', 'center')
      .text(this.config.xAxisLegendLabel)

    g.append('g')
      .attr('class', 'y axis')
      .attr('transform', `translate(${width}, 0)`)
      .selectAll('text')
      .data([yMax, 0])
      .enter()
      .append('text')
      .attr('text-anchor', 'end')
      .attr('y', (d, i) => i * height)
      .attr('dy', '-6px')
      .html((d) => {
        return `
      ${
        this.config.mode !== 'linear'
          ? d === 0
            ? '0'
            : '100'
          : d !== 0
          ? this.hideZero.transform(d)
          : 0
      }
      <tspan class="light">
       ${this.config.mode !== 'linear' ? '%' : ''}
      </tspan>`
      })
  }

  parseToLines(data) {
    const out = {}
    data.forEach((d) => {
      d.values.forEach((val) => {
        if (!out[val.category]) {
          out[val.category] = []
        }
        out[val.category].push({ ...val, filter: d.filter })
      })
    })
    return Object.values(out)
  }

  onSelectTarget($event) {
    this.current = $event
    this.parseData()
    this.draw()
  }

  _prepareComponent() {
    this.options = Object.keys(this._targets).map((t) => ({
      label: this._labels[t] || t,
      value: t,
    }))
    this.current = this.options[0] ? this.options[0] : null
    this.parseData()
    this.draw()
  }

  setConfigChart() {
    if (!this.current) {
      return
    }
    this.historicalService.updateMaps(this.context.vertical)
    this.historicalService.parseCategoryNames()
    const config: any = this.historicalService.chartList.find(
      (d) => d.id === this.current.value,
    ).config
    const defaultUnit = this.config.unit
    this.config = {
      xScale: this._getXScale(config.xAxisSort),
      xAxisLegendLabel: config.xAxisLegendLabel,
      categoriesSort: config.categoriesSort || [],
      labels: this.historicalService.categoryNameMap,
      mode: config.mode,
      barWidth: config.barWidth,
      unit: defaultUnit,
    }
    const colorScale = [
      scaleLinear()
        .range([config.colors[0], config.colors.slice(-1)[0]])
        .domain([0, this.categories.length - 1]),
      scaleLinear()
        .range([config.colorsCompared[0], config.colorsCompared.slice(-1)[0]])
        .domain([0, this.categories.length - 1]),
    ]
    this.config = { ...this.config, colorScale }
  }

  emitCollapse($event) {
    this.onCollapse.emit($event)
    this.collapsed = $event
  }

  _getXScale(sortedArray) {
    const _target = this.targets[this.current.value]
    const target = Array.isArray(_target) ? _target[0] : _target
    if (!target) {
      return []
    }
    const scale = !this.current
      ? []
      : Array.from(new Set(target.values.map((v) => v.filter)))

    scale.sort((a, b) => {
      return sortedArray.indexOf(a) - sortedArray.indexOf(b)
    })
    return scale
  }
}
